import type { Core } from "@/client/core/Core.api";
import type { FetchSpliceSamplesService } from "@/client/core/services/fetch-splice-samples.service";
import type { LibrarySearchService } from "@/client/core/services/library-search.service";
import type { AudioToPresetSearchService } from "@/client/core/services/audio-to-preset-search.service";
import type { SearchWithSoundSimilarService } from "@/client/core/services/search-with-sound-similar.service";
import { kMaxSearchResults } from "@/shared/audio-to-preset-search.api";
import type { SpliceSoundsSearchService } from "@/client/core/services/splice-sounds-search.service";
import type { CoreStorage } from "@/client/core/services/storage.service";
import { readStoredResource } from "@/client/core/services/resources-website.service";
import { createDebouncer } from "@/client/utils/debounce";
import { writeSampleDragPayload } from "@/client/utils/sample-drag-payload";
import type {
    LibrarySearchCategory,
    LibrarySearchRequestBody,
    LibrarySearchSort,
} from "@/shared/library-search.api";
import {
    SpliceAssetCategory,
    SpliceSortType,
    type SpliceSearchResult,
} from "@/shared/splice-search.api";
import { getUploadSizeError } from "@/client/utils/upload-size";
import { toPresetResults } from "./preset-results";
import {
    getSoundsBrowserMode,
    getVisibleSoundsBrowserModes,
    kDefaultSoundsBrowserModeId,
    parseSampleUuids,
    pickSeededMode,
    type SoundsBrowserModeId,
    type SoundsBrowserMode,
} from "./modes/sounds-browser-modes";
import type { SamplePreviewCache } from "./sample-preview-cache";
import { getResourcePlaybackId, type SamplePreviewPlayer } from "./sample-preview-player";
import type {
    SoundsBrowserActions,
    SoundsBrowserFilters,
    SoundsBrowserPrefs,
    SoundsBrowserSeed,
    SoundsBrowserSignals,
    SoundsBrowserState,
} from "./sounds-browser.api";
import { saveSoundsBrowserSession } from "./sounds-browser-session";

export const kSoundsBrowserMinWidth_px = 380;
export const kSoundsBrowserMaxWidth_px = 720;
export const kSoundsBrowserDefaultWidth_px = 440;

/**
 * Two characters is a search; one is a typo in progress. Typing never
 * searches on its own: an Atlantis round trip is too long to spend on every
 * keystroke, so the field waits for Enter.
 */
const kMinQueryLength = 2;
/** Scrolling writes to storage, so it waits for the scroll to settle. */
const kScrollSaveDebounce_ms = 150;
/** Similar (SiSo) returns one unpaged set; this is the most its route allows. */
const kSoundMatchResults = 25;
/** Preset search returns one unpaged set too; its route allows ten. */
const kPresetResults = kMaxSearchResults;

/**
 * Your library and loops by default: what a person already owns is the
 * quickest thing to pull into a tool, and loops are what most Labs tools take.
 */
export const kDefaultSoundsBrowserFilters: SoundsBrowserFilters = {
    isLibraryOnly: true,
    category: SpliceAssetCategory.Loop,
    instruments: [],
    genres: [],
    tags: [],
    key: "",
    chordType: "",
    bpmMin: undefined,
    bpmMax: undefined,
    sort: "",
};

/**
 * Whether the filters say anything Atlantis can search on by themselves. The
 * type alone does not count: the route rejects a search with no words, tags,
 * key or tempo, and a default of Loops must not turn an empty field into one.
 */
export const hasSearchCriteria = (filters: SoundsBrowserFilters): boolean =>
    filters.instruments.length > 0 ||
    filters.genres.length > 0 ||
    filters.tags.length > 0 ||
    filters.key !== "" ||
    filters.chordType !== "" ||
    filters.bpmMin !== undefined ||
    filters.bpmMax !== undefined;

/**
 * Whether any filter differs from its default, which is what "Clear all
 * filters" undoes. The scope (library or all of Splice) is not a filter: it
 * has its own switch, and clearing the filters leaves it where it is.
 */
export const hasActiveFilters = (filters: SoundsBrowserFilters): boolean =>
    filters.category !== kDefaultSoundsBrowserFilters.category ||
    filters.instruments.length > 0 ||
    filters.genres.length > 0 ||
    filters.tags.length > 0 ||
    filters.key !== "" ||
    filters.chordType !== "" ||
    filters.bpmMin !== undefined ||
    filters.bpmMax !== undefined;

/** The playback-state name a seed plays under, so its chip can show it playing. */
export const getSeedPlaybackId = (seed: SoundsBrowserSeed): string =>
    seed.kind === "catalog" ? seed.result.metadata.uuid : getResourcePlaybackId(seed.id);

export const clampSoundsBrowserWidth = (width_px: number): number =>
    Math.round(Math.min(kSoundsBrowserMaxWidth_px, Math.max(kSoundsBrowserMinWidth_px, width_px)));

/** What one round of any mode produces. */
interface SearchOutcome {
    readonly results: readonly SpliceSearchResult[];
    readonly tagSummary: readonly string[];
    readonly hasMore: boolean;
}

export interface SoundsBrowserServices {
    readonly search: SpliceSoundsSearchService;
    readonly fetchSamples: FetchSpliceSamplesService;
    readonly soundMatch: SearchWithSoundSimilarService;
    readonly presets: AudioToPresetSearchService;
    readonly library: LibrarySearchService;
}

/**
 * Results per library page: one Atlantis fetch-by-uuid batch, since the
 * server turns each page's ids into playable previews through it.
 */
const kLibraryResultsPerPage = 16;

const kLibraryCategory: Partial<Record<SpliceAssetCategory, LibrarySearchCategory>> = {
    [SpliceAssetCategory.Loop]: "loop",
    [SpliceAssetCategory.OneShot]: "oneshot",
};

/**
 * The library's default order is what was licensed most recently, which is
 * the gateway's `recency` on a licensed search, and what the plugin's own
 * library view sorts by.
 */
const kLibrarySort: Record<string, LibrarySearchSort> = {
    "": "recency",
    [SpliceSortType.Relevance]: "relevance",
    [SpliceSortType.Popularity]: "popularity",
    [SpliceSortType.Recency]: "recency",
    [SpliceSortType.Random]: "random",
};

/** The dock's filters as a library request. Exported for its test. */
export const toLibrarySearchBody = (
    query: string,
    filters: SoundsBrowserFilters,
    page: number,
): LibrarySearchRequestBody => ({
    query: query.trim(),
    count: kLibraryResultsPerPage,
    page,
    assetCategory: kLibraryCategory[filters.category],
    tags: [...filters.instruments, ...filters.genres, ...filters.tags],
    key: filters.key !== "" ? filters.key : undefined,
    chordType:
        filters.chordType === "major" || filters.chordType === "minor"
            ? filters.chordType
            : undefined,
    bpmMin: filters.bpmMin,
    bpmMax: filters.bpmMax,
    sort: kLibrarySort[filters.sort] ?? "recency",
});

export const createSoundsBrowserActions = (
    core: Core,
    services: SoundsBrowserServices,
    previews: SamplePreviewCache,
    player: SamplePreviewPlayer,
    prefs: CoreStorage<SoundsBrowserPrefs>,
    state: SoundsBrowserState,
    signals: SoundsBrowserSignals,
): SoundsBrowserActions => {
    const { search: service } = services;
    const scrollDebouncer = createDebouncer(kScrollSaveDebounce_ms);
    // Only the newest search speaks. Two in flight can finish out of order and
    // the older one would otherwise replace the list the user asked for last.
    let searchGeneration = 0;
    let scrollTop = 0;
    let didRefreshRestored = false;
    /** The library page the results run to; the catalog's page lives in its service. */
    let libraryPage = 1;

    const currentMode = (): SoundsBrowserMode => getSoundsBrowserMode(state.modeId);

    // The browse carries over to the next page of the site (see
    // sounds-browser-session.ts). Saved whenever the result set or the
    // position in it changes; an empty set clears it.
    /** Whether the list on screen is library pages rather than Atlantis pages. */
    const isLibraryList = (): boolean =>
        currentMode().input === "text" && state.filters.isLibraryOnly;

    const saveSession = (): void => {
        saveSoundsBrowserSession({
            modeId: state.modeId,
            seed: state.seed,
            selectedUuid: state.selectedUuid,
            resume: state.resume,
            query: state.query,
            filters: state.filters,
            results: state.results,
            tagSummary: state.tagSummary,
            page: isLibraryList() ? libraryPage : service.getPage(),
            hasMore: state.hasMore,
            scrollTop,
            savedAt: Date.now(),
        });
    };

    const savePrefs = (): void => {
        prefs.save({ isOpen: state.isOpen, width_px: state.width_px }, undefined);
    };

    /**
     * A dock with nothing in it yet lists what it can without words: the
     * library scope needs no query, so the first open shows the user's own
     * sounds rather than an empty list waiting for one. A browse that is
     * already there, searched or restored, is left alone.
     */
    const searchIfUnstarted: SoundsBrowserActions["searchIfUnstarted"] = () => {
        if (state.hasSearched || state.isSearching) {
            return;
        }
        void search();
    };

    const setIsOpen = (isOpen: boolean): void => {
        if (state.isOpen === isOpen) {
            return;
        }
        state.isOpen = isOpen;
        signals.isOpen.notify(isOpen);
        savePrefs();
        if (isOpen) {
            searchIfUnstarted();
        } else {
            player.stop();
        }
    };

    const pushFiltersToService = (): void => {
        const { filters } = state;
        service.setAssetCategory(filters.category);
        service.setInstrument("");
        service.setGenre("");
        service.setQueryTags([...filters.instruments, ...filters.genres, ...filters.tags]);
        service.setKey(filters.key);
        service.setChordType(filters.chordType);
        service.setBpmMin(filters.bpmMin);
        service.setBpmMax(filters.bpmMax);
        service.setSort(filters.sort);
    };

    /** The audio behind a seed, and the tempo the catalog knows for it. */
    const resolveSeedFile = async (
        seed: SoundsBrowserSeed,
    ): Promise<{ file: File; bpm: number | undefined } | Error> => {
        if (seed.kind === "catalog") {
            const preview = await previews.ensure(seed.result);
            if (preview instanceof Error) {
                return preview;
            }
            return {
                file: preview.file,
                bpm: seed.result.metadata.bpm > 0 ? seed.result.metadata.bpm : undefined,
            };
        }
        const resource = core.actions.getAudioResource(seed.id);
        if (resource === undefined) {
            return new Error("The sample you searched from is no longer available.");
        }
        return { file: resource.file, bpm: resource.metadata.bpm ?? undefined };
    };

    const canSearch = (): boolean => {
        const mode = currentMode();
        switch (mode.input) {
            case "text":
                // The library is finite and the user's own, so it can be
                // browsed whole; the catalog needs a word or a filter.
                return (
                    state.filters.isLibraryOnly ||
                    state.query.trim().length >= kMinQueryLength ||
                    hasSearchCriteria(state.filters)
                );
            case "audio":
            case "audio-only":
                return state.seed !== undefined;
            case "uuids":
                return parseSampleUuids(state.query).length > 0;
        }
    };

    const publishEmpty = (): void => {
        state.results = [];
        state.tagSummary = [];
        state.hasMore = false;
        state.isSearching = false;
        state.error = undefined;
        signals.isSearching.notify(false);
        signals.results.notify(state.results);
        signals.tagSummary.notify(state.tagSummary);
        signals.hasMore.notify(false);
        signals.error.notify(undefined);
    };

    /** One round of the current mode. Each branch is one capability call. */
    const runSearch = async (): Promise<SearchOutcome | Error> => {
        const mode = currentMode();
        const query = state.query.trim();
        switch (mode.input) {
            case "text": {
                if (state.filters.isLibraryOnly) {
                    libraryPage = 1;
                    const response = await services.library.search(
                        toLibrarySearchBody(query, state.filters, libraryPage),
                    );
                    return response instanceof Error
                        ? response
                        : {
                              results: response.results,
                              tagSummary: response.tagSummary,
                              hasMore: response.hasMore,
                          };
                }
                service.setSeedFile(undefined);
                service.setSeedBpm(undefined);
                pushFiltersToService();
                const response = await service.search(query);
                return response instanceof Error
                    ? response
                    : { ...response, hasMore: response.results.length > 0 };
            }
            case "audio": {
                if (state.seed === undefined) {
                    return new Error("Drop a sample to search from.");
                }
                const seed = await resolveSeedFile(state.seed);
                if (seed instanceof Error) {
                    return seed;
                }
                service.setSeedFile(seed.file);
                service.setSeedBpm(seed.bpm);
                pushFiltersToService();
                // The field is the text search, so its words do not steer the seed.
                const response = await service.search("");
                return response instanceof Error
                    ? response
                    : { ...response, hasMore: response.results.length > 0 };
            }
            case "audio-only": {
                if (state.seed === undefined) {
                    return new Error("Drop a sample to match.");
                }
                const seed = await resolveSeedFile(state.seed);
                if (seed instanceof Error) {
                    return seed;
                }
                if (mode.id === "presets") {
                    const response = await services.presets.search({
                        file: seed.file,
                        k: kPresetResults,
                    });
                    return response instanceof Error
                        ? response
                        : { results: toPresetResults(response), tagSummary: [], hasMore: false };
                }
                const response = await services.soundMatch.search({
                    inputFile: seed.file,
                    maxResults: kSoundMatchResults,
                });
                return response instanceof Error
                    ? response
                    : { results: response.results, tagSummary: [], hasMore: false };
            }
            case "uuids": {
                const response = await services.fetchSamples.fetchSamples(parseSampleUuids(query));
                return response instanceof Error
                    ? response
                    : { results: response.results, tagSummary: [], hasMore: false };
            }
        }
    };

    const search: SoundsBrowserActions["search"] = async () => {
        const generation = ++searchGeneration;

        if (!canSearch()) {
            // Nothing to search from yet, so the mode's own call to action
            // shows rather than "no sounds found".
            state.hasSearched = false;
            publishEmpty();
            saveSession();
            return;
        }

        state.isSearching = true;
        state.hasSearched = true;
        state.error = undefined;
        // A page that was still arriving belongs to the old search; the
        // flag must not outlive it, or the footer says "loading" forever.
        if (state.isLoadingMore) {
            state.isLoadingMore = false;
            signals.isLoadingMore.notify(false);
        }
        signals.isSearching.notify(true);
        signals.error.notify(undefined);

        const outcome = await runSearch();
        if (generation !== searchGeneration) {
            return;
        }

        state.isSearching = false;
        state.isRestored = false;
        scrollTop = 0;
        if (outcome instanceof Error) {
            state.results = [];
            state.hasMore = false;
            state.error = outcome.message;
            signals.results.notify(state.results);
            signals.hasMore.notify(false);
            signals.error.notify(state.error);
        } else {
            state.results = outcome.results;
            state.tagSummary = outcome.tagSummary;
            state.hasMore = outcome.hasMore;
            signals.results.notify(state.results);
            signals.tagSummary.notify(state.tagSummary);
            signals.hasMore.notify(state.hasMore);
        }
        signals.isSearching.notify(false);
        saveSession();
    };

    const loadMore: SoundsBrowserActions["loadMore"] = async () => {
        if (!currentMode().isPaged || state.isSearching || state.isLoadingMore || !state.hasMore) {
            return;
        }
        const generation = searchGeneration;
        const before = state.results.length;
        state.isLoadingMore = true;
        signals.isLoadingMore.notify(true);

        if (currentMode().input === "text" && state.filters.isLibraryOnly) {
            const response = await services.library.search(
                toLibrarySearchBody(state.query, state.filters, libraryPage + 1),
            );
            if (generation !== searchGeneration) {
                return;
            }
            state.isLoadingMore = false;
            if (response instanceof Error) {
                state.error = response.message;
                signals.error.notify(state.error);
            } else {
                libraryPage += 1;
                state.results = [...state.results, ...response.results];
                state.hasMore = response.hasMore && response.results.length > 0;
                if (response.results.length > 0) {
                    signals.resultsAppended.notify(response.results);
                }
                signals.hasMore.notify(state.hasMore);
                saveSession();
            }
            signals.isLoadingMore.notify(false);
            return;
        }

        const response = await service.loadMore();
        if (generation !== searchGeneration) {
            return;
        }

        state.isLoadingMore = false;
        if (response instanceof Error) {
            state.error = response.message;
            signals.error.notify(state.error);
        } else if (response === undefined) {
            state.hasMore = false;
            signals.hasMore.notify(false);
        } else {
            const appended = response.results.slice(before);
            state.results = response.results;
            state.hasMore = appended.length > 0;
            if (appended.length > 0) {
                signals.resultsAppended.notify(appended);
            }
            signals.hasMore.notify(state.hasMore);
            saveSession();
        }
        signals.isLoadingMore.notify(false);
    };

    const updateFilters = (patch: Partial<SoundsBrowserFilters>): void => {
        state.filters = { ...state.filters, ...patch };
        signals.filters.notify(state.filters);
        if (currentMode().usesFilters) {
            void search();
        }
    };

    const setMode: SoundsBrowserActions["setMode"] = (modeId) => {
        if (state.modeId === modeId) {
            return;
        }
        state.modeId = modeId;
        signals.mode.notify(modeId);
        // The seed and the words carry across; the mode decides which of
        // them count. What was found under the old mode does not.
        void search();
    };

    const applyAudience: SoundsBrowserActions["applyAudience"] = (audienceState) => {
        const visible = getVisibleSoundsBrowserModes(audienceState);
        state.visibleModes = visible;
        signals.visibleModes.notify(visible);
        // Not before the audience is known: a restored session in an R&D
        // mode must not be thrown away by the default state that precedes
        // the flags.
        if (!audienceState.isReady) {
            return;
        }
        if (!visible.some((mode) => mode.id === state.modeId)) {
            setMode(kDefaultSoundsBrowserModeId);
        }
    };

    const setSeed = (seed: SoundsBrowserSeed, preferredModeId?: SoundsBrowserModeId): void => {
        state.seed = seed;
        signals.seed.notify(seed);
        // A seed searches the catalog, so it leaves the library scope, where
        // the sound zone and its tabs would otherwise stay hidden.
        if (state.filters.isLibraryOnly) {
            state.filters = { ...state.filters, isLibraryOnly: false, sort: "" };
            signals.filters.notify(state.filters);
        }
        const seededModeId = pickSeededMode(state.visibleModes, {
            currentModeId: state.modeId,
            preferredModeId,
        });
        if (seededModeId !== undefined && seededModeId !== state.modeId) {
            // setMode searches.
            setMode(seededModeId);
            return;
        }
        void search();
    };

    const seedFromResult: SoundsBrowserActions["seedFromResult"] = (result, preferredModeId) => {
        player.stop();
        select(result.metadata.uuid);
        setSeed({ kind: "catalog", result }, preferredModeId);
    };

    const seedFromFile: SoundsBrowserActions["seedFromFile"] = (file) => {
        player.stop();
        const sizeError = getUploadSizeError(file);
        if (sizeError !== undefined) {
            state.error = sizeError;
            signals.error.notify(state.error);
            return;
        }
        void core.actions.addFiles([{ file, type: "wav", metadata: undefined }]).then((added) => {
            if (added instanceof Error) {
                state.error = added.message;
                signals.error.notify(state.error);
                return;
            }
            if (added.length === 0) {
                return;
            }
            const resource = added[0];
            setSeed({
                kind: "resource",
                id: resource.id,
                name: resource.file.name,
                bpm: resource.metadata.bpm,
                namespace: core.appDefinition.directory,
            });
        });
    };

    const clearSeed: SoundsBrowserActions["clearSeed"] = () => {
        if (state.seed === undefined) {
            return;
        }
        state.seed = undefined;
        signals.seed.notify(undefined);
        // A seeded mode has nothing to search from now; the words still do.
        const mode = currentMode();
        if (mode.input === "audio" || mode.input === "audio-only") {
            setMode(kDefaultSoundsBrowserModeId);
            return;
        }
        void search();
    };

    const toggleSeedPreview: SoundsBrowserActions["toggleSeedPreview"] = () => {
        const seed = state.seed;
        if (seed === undefined) {
            return;
        }
        const playback = player.getState();
        if (playback.isPlaying && playback.uuid === getSeedPlaybackId(seed)) {
            player.stop();
            return;
        }
        if (seed.kind === "catalog") {
            player.play(seed.result);
        } else {
            player.playResource(seed.id);
        }
    };

    const warm: SoundsBrowserActions["warm"] = (result) => {
        void previews.ensure(result);
    };

    const select = (uuid: string): void => {
        if (state.selectedUuid === uuid) {
            return;
        }
        state.selectedUuid = uuid;
        signals.selected.notify(uuid);
        saveSession();
    };

    const clearResume = (): void => {
        if (state.resume === undefined) {
            return;
        }
        state.resume = undefined;
        signals.resume.notify(undefined);
    };

    const togglePreview: SoundsBrowserActions["togglePreview"] = (result) => {
        const { uuid } = result.metadata;
        const playback = player.getState();
        if (playback.isPlaying && playback.uuid === uuid) {
            player.stop();
            return;
        }
        select(uuid);
        const resume = state.resume;
        if (resume?.uuid === uuid) {
            // The previous page was playing this: carry on from where it was.
            clearResume();
            player.play(result, resume.position_s);
            return;
        }
        player.play(result);
    };

    const saveForNavigation: SoundsBrowserActions["saveForNavigation"] = () => {
        const resume = player.getResumeState();
        if (resume === undefined) {
            clearResume();
        } else {
            state.resume = resume;
        }
        // The audition resource does not cross pages (resources are
        // namespaced per app), so it goes now and the next page refetches.
        player.release();
        saveSession();
    };

    const tryResume: SoundsBrowserActions["tryResume"] = () => {
        const resume = state.resume;
        if (resume === undefined) {
            return;
        }
        // Audio may only start on its own where the browser says the user has
        // already engaged with this document. Otherwise the row keeps the
        // resume and one press picks it up.
        if (!navigator.userActivation.hasBeenActive) {
            return;
        }
        const result = state.results.find((r) => r.metadata.uuid === resume.uuid);
        if (result !== undefined) {
            togglePreview(result);
        }
    };

    const beginDrag: SoundsBrowserActions["beginDrag"] = (result, dataTransfer) => {
        writeSampleDragPayload(dataTransfer, result, previews.peek(result.metadata.uuid)?.file);
        warm(result);
    };

    /**
     * A dropped file was stored by the app it was dropped in, and this page's
     * resources cannot see it. Read it from that app's namespace and add it
     * here, so the seed keeps working across the site. If it is gone, say so
     * and go back to words rather than fail every seeded search.
     */
    const adoptSeed = async (
        seed: Extract<SoundsBrowserSeed, { kind: "resource" }>,
    ): Promise<void> => {
        if (core.actions.getAudioResource(seed.id) !== undefined) {
            return;
        }
        const stored = await readStoredResource(seed.namespace, seed.id).catch(() => undefined);
        const added =
            stored === undefined
                ? undefined
                : await core.actions.addFiles([
                      {
                          file: new File([stored.file], seed.name, { type: stored.file.type }),
                          type: "wav",
                          metadata: undefined,
                      },
                  ]);
        if (state.seed?.kind !== "resource" || state.seed.id.value !== seed.id.value) {
            return;
        }
        if (added === undefined || added instanceof Error || added.length === 0) {
            state.seed = undefined;
            signals.seed.notify(undefined);
            state.error = "The dropped sample did not carry over to this page. Drop it again.";
            signals.error.notify(state.error);
            if (currentMode().input !== "text") {
                setMode(kDefaultSoundsBrowserModeId);
            }
            return;
        }
        state.seed = {
            ...seed,
            id: added[0].id,
            bpm: added[0].metadata.bpm,
            namespace: core.appDefinition.directory,
        };
        signals.seed.notify(state.seed);
        saveSession();
    };

    const restoreSession: SoundsBrowserActions["restoreSession"] = (session) => {
        searchGeneration += 1;
        state.modeId = session.modeId;
        state.seed = session.seed;
        state.selectedUuid = session.selectedUuid;
        state.resume = session.resume;
        state.query = session.query;
        state.filters = session.filters;
        state.results = session.results;
        state.tagSummary = session.tagSummary;
        state.hasMore = session.hasMore;
        state.hasSearched = true;
        state.isRestored = true;
        state.isSearching = false;
        state.error = undefined;
        scrollTop = session.scrollTop;
        pushFiltersToService();
        // The two lists page separately; the saved page belongs to whichever
        // the results came from, so the next "load more" continues from it.
        if (isLibraryList()) {
            libraryPage = session.page;
            service.restoreSession(session.query, session.results, 1);
        } else {
            libraryPage = 1;
            service.restoreSession(session.query, session.results, session.page);
        }
        if (
            state.seed?.kind === "resource" &&
            state.seed.namespace !== core.appDefinition.directory
        ) {
            void adoptSeed(state.seed);
        }
        signals.mode.notify(state.modeId);
        signals.seed.notify(state.seed);
        signals.selected.notify(state.selectedUuid);
        signals.resume.notify(state.resume);
        signals.query.notify(state.query);
        signals.filters.notify(state.filters);
        signals.tagSummary.notify(state.tagSummary);
        signals.results.notify(state.results);
        signals.hasMore.notify(state.hasMore);
    };

    const setScrollOffset: SoundsBrowserActions["setScrollOffset"] = (nextScrollTop) => {
        scrollTop = Math.max(0, Math.round(nextScrollTop));
        scrollDebouncer.call(saveSession);
    };

    // A restored preview link that has gone stale is the one case where a
    // search is worth re-running on the user's behalf. Once: if the fresh
    // links fail too, the problem is not the age of the session.
    const reportPreviewFailure: SoundsBrowserActions["reportPreviewFailure"] = () => {
        if (!state.isRestored || didRefreshRestored) {
            return;
        }
        didRefreshRestored = true;
        void search();
    };

    player.onState((playback) => signals.playback.notify(playback));

    return {
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen(!state.isOpen),
        setWidth: (width_px) => {
            const clamped = clampSoundsBrowserWidth(width_px);
            if (clamped === state.width_px) {
                return;
            }
            state.width_px = clamped;
            signals.width.notify(clamped);
            savePrefs();
        },
        setMode,
        applyAudience,
        seedFromFile,
        seedFromResult,
        clearSeed,
        toggleSeedPreview,
        setQuery: (query) => {
            if (state.query === query) {
                return;
            }
            state.query = query;
            signals.query.notify(query);
        },
        submitQuery: () => {
            // The field is the text search whatever the zone is doing: words
            // and Enter in a seeded mode leave it for Search.
            const mode = currentMode();
            if (mode.input === "audio" || mode.input === "audio-only") {
                setMode(kDefaultSoundsBrowserModeId);
                return;
            }
            void search();
        },
        search,
        searchIfUnstarted,
        loadMore,
        setCategory: (category) => updateFilters({ category }),
        // A scope switch is a fresh start: the words clear with the sort, since
        // each scope has its own default order and what was typed for one is
        // rarely what is wanted from the other. The scope belongs to the text
        // search: choosing it from a seeded or id mode moves to Search, or the
        // list would claim a library it never asked for.
        setLibraryOnly: (isLibraryOnly) => {
            const didSwitch = state.filters.isLibraryOnly !== isLibraryOnly;
            state.filters = { ...state.filters, isLibraryOnly, sort: "" };
            signals.filters.notify(state.filters);
            if (didSwitch && state.query !== "") {
                state.query = "";
                signals.query.notify(state.query);
            }
            if (currentMode().input !== "text") {
                setMode(kDefaultSoundsBrowserModeId);
                return;
            }
            void search();
        },
        setInstruments: (instruments) => updateFilters({ instruments }),
        setGenres: (genres) => updateFilters({ genres }),
        toggleTag: (tag) =>
            updateFilters({
                tags: state.filters.tags.includes(tag)
                    ? state.filters.tags.filter((t) => t !== tag)
                    : [...state.filters.tags, tag],
            }),
        setKey: (key, chordType) => updateFilters({ key, chordType }),
        setBpmRange: (bpmMin, bpmMax) => updateFilters({ bpmMin, bpmMax }),
        setSort: (sort) => updateFilters({ sort }),
        clearFilters: () =>
            updateFilters({
                ...kDefaultSoundsBrowserFilters,
                isLibraryOnly: state.filters.isLibraryOnly,
            }),
        warm,
        togglePreview,
        stopPreview: () => player.stop(),
        beginDrag,
        restoreSession,
        setScrollOffset,
        reportPreviewFailure,
        saveForNavigation,
        tryResume,
    };
};
